import React, { useEffect, useMemo } from 'react'
import type { Collection, ContentStatus } from './types'
import { STATUS_CONFIG } from './types'

type KnowledgeStatsProps = {
  open: boolean
  onClose: () => void
  collections: Collection[]
  statusCounts: Record<ContentStatus, number>
  totalContents: number
  orphanCount: number
}

/**
 * Modal de statistiques unifiées (collections + contenus)
 * Responsabilité unique : affichage des métriques
 */
export default function KnowledgeStats({
  open,
  onClose,
  collections,
  statusCounts,
  totalContents,
  orphanCount,
}: KnowledgeStatsProps) {
  // Fermer avec Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Stats collections
  const collectionStats = useMemo(() => {
    const manual = collections.filter(c => c.source === 'manual').length
    const wordpress = collections.filter(c => c.source === 'wordpress').length
    const total = collections.length
    return {
      total,
      manual,
      wordpress,
      ratioManual: total ? Math.round((manual / total) * 100) : 0,
      ratioWordpress: total ? Math.round((wordpress / total) * 100) : 0,
    }
  }, [collections])

  // Stats contenus
  const contentStats = useMemo(() => {
    const indexed = statusCounts.indexed || 0
    const transcribed = statusCounts.transcribed || 0
    const processing = (statusCounts.transcribing || 0) + (statusCounts.indexing || 0)
    const pending = statusCounts.uploaded || 0
    const errors = statusCounts.error || 0
    const ready = indexed + transcribed
    return {
      total: totalContents,
      indexed,
      transcribed,
      processing,
      pending,
      errors,
      ready,
      orphan: orphanCount,
      healthRate: totalContents ? Math.round((ready / totalContents) * 100) : 0,
    }
  }, [statusCounts, totalContents, orphanCount])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal drawer */}
      <aside className="absolute inset-y-0 right-0 w-full max-w-md border-l border-emerald-100/70 bg-white/95 shadow-2xl dark:border-emerald-900/50 dark:bg-zinc-950/95">
        <div className="flex h-full flex-col">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-emerald-100/70 px-6 py-4 dark:border-emerald-900/50">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                Statistiques
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Vue d'ensemble de votre base de connaissances
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full border border-zinc-200/70 px-3 py-1 text-sm text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Fermer
            </button>
          </header>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Section Collections */}
            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                Collections
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label="Total"
                  value={collectionStats.total}
                  color="emerald"
                />
                <StatCard
                  label="Manuelles"
                  value={collectionStats.manual}
                  suffix={`${collectionStats.ratioManual}%`}
                  color="emerald"
                />
                <StatCard
                  label="WordPress"
                  value={collectionStats.wordpress}
                  suffix={`${collectionStats.ratioWordpress}%`}
                  color="sky"
                />
                <StatCard
                  label="Contenus orphelins"
                  value={contentStats.orphan}
                  color="amber"
                />
              </div>
            </section>

            {/* Section Contenus */}
            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                Contenus
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label="Total"
                  value={contentStats.total}
                  color="emerald"
                />
                <StatCard
                  label="Prêts"
                  value={contentStats.ready}
                  suffix={`${contentStats.healthRate}%`}
                  color="emerald"
                />
                <StatCard
                  label="Indexés"
                  value={contentStats.indexed}
                  color="emerald"
                />
                <StatCard
                  label="Transcrits"
                  value={contentStats.transcribed}
                  color="blue"
                />
                <StatCard
                  label="En traitement"
                  value={contentStats.processing}
                  color="blue"
                  pulse={contentStats.processing > 0}
                />
                <StatCard
                  label="En attente"
                  value={contentStats.pending}
                  color="zinc"
                />
                <StatCard
                  label="En erreur"
                  value={contentStats.errors}
                  color="red"
                />
              </div>
            </section>

            {/* Jauge de santé */}
            <section className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                Santé globale
              </h3>
              <div className="rounded-2xl border border-emerald-100/70 bg-white/80 p-4 dark:border-emerald-900/50 dark:bg-zinc-900/80">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-600 dark:text-zinc-300">
                    Contenus exploitables
                  </span>
                  <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                    {contentStats.healthRate}%
                  </span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                    style={{ width: `${contentStats.healthRate}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                  {contentStats.ready} contenus sur {contentStats.total} sont prêts à être utilisés par vos agents.
                </p>
              </div>
            </section>

            {/* Répartition par statut */}
            <section className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                Répartition par statut
              </h3>
              <div className="space-y-2">
                {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                  const count = statusCounts[status as ContentStatus] || 0
                  const percent = contentStats.total ? Math.round((count / contentStats.total) * 100) : 0
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <span className={`h-2 w-2 rounded-full ${config.dotColor}`} />
                      <span className="flex-1 text-sm text-zinc-600 dark:text-zinc-300">
                        {config.label}
                      </span>
                      <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                        {count}
                      </span>
                      <span className="w-12 text-right text-xs text-zinc-400">
                        {percent}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </section>
          </div>
        </div>
      </aside>
    </div>
  )
}

type StatCardProps = {
  label: string
  value: number
  suffix?: string
  color: 'emerald' | 'sky' | 'amber' | 'red' | 'blue' | 'zinc'
  pulse?: boolean
}

function StatCard({ label, value, suffix, color, pulse }: StatCardProps) {
  const colorClasses = {
    emerald: 'border-emerald-100/70 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-900/30',
    sky: 'border-sky-100/70 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-900/30',
    amber: 'border-amber-100/70 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-900/30',
    red: 'border-red-100/70 bg-red-50/60 dark:border-red-900/50 dark:bg-red-900/30',
    blue: 'border-blue-100/70 bg-blue-50/60 dark:border-blue-900/50 dark:bg-blue-900/30',
    zinc: 'border-zinc-200/70 bg-zinc-50/60 dark:border-zinc-800 dark:bg-zinc-900/30',
  }

  const valueClasses = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    sky: 'text-sky-700 dark:text-sky-300',
    amber: 'text-amber-700 dark:text-amber-300',
    red: 'text-red-700 dark:text-red-300',
    blue: 'text-blue-700 dark:text-blue-300',
    zinc: 'text-zinc-700 dark:text-zinc-300',
  }

  return (
    <div className={`rounded-2xl border p-3 ${colorClasses[color]} ${pulse ? 'animate-pulse' : ''}`}>
      <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${valueClasses[color]}`}>
        {value.toLocaleString('fr-FR')}
        {suffix && (
          <span className="ml-1 text-sm font-normal text-zinc-400 dark:text-zinc-500">
            {suffix}
          </span>
        )}
      </div>
    </div>
  )
}
